import { useState, useEffect, useCallback } from "react";
import { getInstitutionOverview, getInstitutionSocialImpact } from "Explore/Services/Overview";

const useOverviewData = (institutionId) => {
  const [data, setData] = useState(null);
  const [socialImpactData, setSocialImpactData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSocialImpact, setIsLoadingSocialImpact] = useState(false);
  const [error, setError] = useState(null);
  const [socialImpactError, setSocialImpactError] = useState(null);

  const fetchOverview = useCallback(async (sections = "all") => {
    if (!institutionId) return;
    
    if (sections === "all") {
      setIsLoading(true);
      setError(null);
    }
    
    try {
      const apiRes = await getInstitutionOverview(institutionId, sections);
      const res = Array.isArray(apiRes) ? apiRes[0] : apiRes;
      
      if (!res) return;

      if (sections === "all") {
        setData(res);
      } else {
        const requestedSections = sections.split(',');
        setData(prev => {
          if (!prev) return res;
          const newData = { ...prev };
          requestedSections.forEach(sec => {
            if (res[sec] !== undefined) {
               newData[sec] = res[sec];
            }
          });
          // Remove retry sections from errors
          if (newData.errors) {
            requestedSections.forEach(sec => {
               delete newData.errors[sec];
            });
          }
          if (res.errors) {
            newData.errors = { ...newData.errors, ...res.errors };
          }
          return newData;
        });
      }
    } catch (err) {
      if (sections === "all") {
        setError(err?.message || "Failed to fetch overview");
      }
    } finally {
      if (sections === "all") {
        setIsLoading(false);
      }
    }
  }, [institutionId]);

  const fetchSocialImpact = useCallback(async () => {
    if (!institutionId) return;
    setIsLoadingSocialImpact(true);
    setSocialImpactError(null);
    try {
      const apiRes = await getInstitutionSocialImpact(institutionId);
      const res = Array.isArray(apiRes) ? apiRes[0] : apiRes;
      setSocialImpactData(res);
    } catch (err) {
      setSocialImpactError(err?.message || "Failed to fetch social impact");
    } finally {
      setIsLoadingSocialImpact(false);
    }
  }, [institutionId]);

  useEffect(() => {
    if (institutionId) {
      fetchOverview();
      fetchSocialImpact();
    }
  }, [institutionId, fetchOverview, fetchSocialImpact]);

  const retrySection = useCallback((sectionKey) => {
    fetchOverview(sectionKey);
  }, [fetchOverview]);

  return {
    data,
    socialImpactData,
    isLoading,
    isLoadingSocialImpact,
    error,
    socialImpactError,
    retrySection,
    fetchSocialImpact
  };
};

export default useOverviewData;
